import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";

const TONES = [
  { v: "friendly", l: "Friendly" },
  { v: "news", l: "News" },
  { v: "professional", l: "Professional" },
  { v: "funny", l: "Funny" },
];
const errMsg = (e) => e?.response?.data?.message || e.message || "Something went wrong";
const pad = (n) => String(n).padStart(2, "0");

const AutopilotWizard = ({ show, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [language, setLanguage] = useState("English");
  const [tone, setTone] = useState("friendly");
  const [postsPerDay, setPostsPerDay] = useState(5);
  const [windowStart, setWindowStart] = useState(9);
  const [windowEnd, setWindowEnd] = useState(21);
  const [breakingNow, setBreakingNow] = useState(false);
  const [mode, setMode] = useState("review");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setStep(1); setUrl(""); setName(""); setPreview(null); setMode("review"); setBreakingNow(false);
    axiosInstance.get("channels").then(({ data }) => {
      const list = data.data || [];
      setChannels(list);
      setChannelId(list[0]?._id || "");
    }).catch(() => {});
  }, [show]);

  const checkLink = async () => {
    if (!url.trim()) return;
    setChecking(true); setPreview(null);
    try {
      const { data } = await axiosInstance.post("autopilot/preview", { url: url.trim() });
      setPreview(data);
    } catch (e) {
      swal("Cannot read this link", errMsg(e), "error");
    }
    setChecking(false);
  };

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.post("autopilot/sources", {
        channelId, url: url.trim(), name: name.trim(), mode, postsPerDay: Number(postsPerDay),
        windowStart: Number(windowStart), windowEnd: Number(windowEnd), breakingNow,
        language, tone, tzOffsetMin: -new Date().getTimezoneOffset(),
      });
      // kick off the first check so drafts appear right away (ignore errors, e.g. no AI key yet)
      axiosInstance.post(`autopilot/sources/${data.data._id}/run`).catch(() => {});
      swal("Autopilot is on", "First posts will appear in your Review Inbox in a minute.", "success");
      onCreated();
      onClose();
    } catch (e) {
      swal("Error", errMsg(e), "error");
    }
    setSaving(false);
  };

  const canNext = (step === 1 && channelId) || (step === 2 && preview) || step === 3 || step === 4;
  const typeLabel = { rss: "RSS feed", web: "Web page", youtube: "YouTube", reddit: "Reddit", telegram: "Telegram" };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header>
        <Modal.Title>Set up Autopilot <small className="text-muted ml-2">Step {step} of 4</small></Modal.Title>
        <button className="close" onClick={onClose}><span>×</span></button>
      </Modal.Header>
      <Modal.Body>
        <div className="progress mb-4" style={{ height: 6 }}>
          <div className="progress-bar" style={{ width: `${step * 25}%`, background: "#17556D" }} />
        </div>

        {step === 1 && (
          <>
            <h5>Where should the posts go?</h5>
            {channels.length === 0 ? (
              <p className="text-muted">You have no channels yet. Go to Channel Scheduler, click Sync Channels or add one, then come back.</p>
            ) : (
              <select className="form-control" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                {channels.map((c) => <option key={c._id} value={c._id}>📢 {c.name}</option>)}
              </select>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <h5>Where does the content come from?</h5>
            <p className="text-muted small mb-2">Paste any link: a website or blog, an RSS feed, a YouTube channel, a subreddit, or a public Telegram channel (t.me/name).</p>
            <div className="input-group mb-2">
              <input className="form-control" placeholder="https://example.com/feed" value={url}
                onChange={(e) => { setUrl(e.target.value); setPreview(null); }} />
              <button className="btn btn-primary" onClick={checkLink} disabled={checking || !url.trim()}>
                {checking ? <Spinner animation="border" size="sm" /> : "Check link"}
              </button>
            </div>
            {preview && (
              <div className="border rounded p-3 mt-3" style={{ background: "#f8f9fa" }}>
                <div className="mb-2">
                  <span className="badge badge-success mr-2">Works</span>
                  Detected: <strong>{typeLabel[preview.type] || preview.type}</strong>. Latest items:
                </div>
                {preview.items.map((i, idx) => (
                  <div key={idx} className="d-flex gap-2 mb-2">
                    {i.imageUrl && <img src={i.imageUrl} alt="" referrerPolicy="no-referrer" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }} onError={(e) => (e.target.style.display = "none")} />}
                    <div className="small"><strong>{i.title || "(no title)"}</strong><div className="text-muted">{i.text}</div></div>
                  </div>
                ))}
                <input className="form-control form-control-sm mt-2" placeholder="Name for this source (optional)" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <h5>How should it sound?</h5>
            <div className="row">
              <div className="col-md-6 form-group">
                <label className="font-w500">Language</label>
                <input className="form-control" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="English, Hindi, Gujarati..." />
              </div>
              <div className="col-md-6 form-group">
                <label className="font-w500">Tone</label>
                <div className="d-flex flex-wrap gap-2">
                  {TONES.map((t) => (
                    <button key={t.v} type="button" onClick={() => setTone(t.v)}
                      className={`btn btn-sm ${tone === t.v ? "btn-primary" : "btn-outline-secondary"}`}>{t.l}</button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-muted small mb-0">You can fine-tune this later in AI &amp; Style: signature, banned words, example posts. The AI also learns from every post you approve or reject.</p>
          </>
        )}

        {step === 4 && (
          <>
            <h5>How often should it post?</h5>
            <div className="row">
              <div className="col-md-4 form-group">
                <label className="font-w500">Posts per day</label>
                <input type="number" min="1" max="50" className="form-control" value={postsPerDay} onChange={(e) => setPostsPerDay(e.target.value)} />
              </div>
              <div className="col-md-4 form-group">
                <label className="font-w500">From</label>
                <select className="form-control" value={windowStart} onChange={(e) => setWindowStart(e.target.value)}>
                  {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{pad(h)}:00</option>)}
                </select>
              </div>
              <div className="col-md-4 form-group">
                <label className="font-w500">Until</label>
                <select className="form-control" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)}>
                  {Array.from({ length: 24 }, (_, h) => h + 1).map((h) => <option key={h} value={h}>{pad(h % 24)}:00</option>)}
                </select>
              </div>
            </div>
            <div className="form-check mb-3">
              <input id="bn" type="checkbox" className="form-check-input" checked={breakingNow} onChange={(e) => setBreakingNow(e.target.checked)} />
              <label htmlFor="bn" className="form-check-label">Post new items right away (only in Full Auto mode)</label>
            </div>
            <label className="font-w500">Who approves posts?</label>
            <div className="d-flex flex-column gap-2">
              <label className={`border rounded p-2 ${mode === "review" ? "border-primary" : ""}`} style={{ cursor: "pointer" }}>
                <input type="radio" checked={mode === "review"} onChange={() => setMode("review")} /> <strong>Review first</strong> (recommended)
                <div className="text-muted small ml-4">You approve each post with one tap. After 10 clean approvals we will suggest Full Auto.</div>
              </label>
              <label className={`border rounded p-2 ${mode === "auto" ? "border-primary" : ""}`} style={{ cursor: "pointer" }}>
                <input type="radio" checked={mode === "auto"} onChange={() => setMode("auto")} /> <strong>Full Auto</strong>
                <div className="text-muted small ml-4">Posts go out by themselves when the AI quality score is high enough. Weak posts still wait for your review.</div>
              </label>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step > 1 && <button className="btn btn-outline-secondary" onClick={() => setStep(step - 1)}>Back</button>}
        {step < 4 && <button className="btn btn-primary" disabled={!canNext} onClick={() => setStep(step + 1)}>Next</button>}
        {step === 4 && (
          <button className="btn btn-primary" disabled={saving} onClick={finish}>
            {saving ? <Spinner animation="border" size="sm" /> : "Start Autopilot"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AutopilotWizard;
